import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaStar } from "react-icons/fa";
import { LazyLoadImage } from "react-lazy-load-image-component";
import isEmpty from "../utils/isEmpty";

const AgentReviewsCard = ({ AgentData, averageRatings }) => {
  const navigate = useNavigate();
  const [showAllReviews, setShowAllReviews] = useState(false);

  const reviews = AgentData?.reviews ?? [];
  const displayReviews = showAllReviews ? reviews : reviews.slice(0, 3);
  const totalCount = reviews.length;
  const hasMore = totalCount > 3;

  const rating =
    averageRatings != null && !isNaN(Number(averageRatings))
      ? Number(averageRatings).toFixed(1)
      : totalCount > 0
        ? "5.0"
        : "0";
  const firstName = AgentData?.first_name ?? "Agent";
  const fullName =
    [AgentData?.first_name, AgentData?.last_name].filter(Boolean).join(" ") ||
    firstName;
  const agencyName =
    AgentData?.agency_id?.principal_name ??
    AgentData?.agency_id?.agency_name ??
    "Agency";

  const formatReviewDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const getInitial = (item) => {
    const first = String(item?.client_firstname ?? "").trim();
    const last = String(item?.client_lastname ?? "").trim();
    const firstInitial = first.charAt(0).toUpperCase();
    const lastInitial = last.charAt(0).toUpperCase();
    const initials = `${firstInitial}${lastInitial}`.trim();
    return initials || "A";
  };

  const getReviewerName = (item) => {
    const name = [item?.client_firstname, item?.client_lastname]
      .filter(Boolean)
      .join(" ")
      .trim();
    return name || "Anonymous";
  };

  const getReviewStars = (item) => {
    const s = item?.star ?? item?.rating ?? 5;
    const n = Math.min(5, Math.max(0, Number(s) || 0));
    return n;
  };

  if (isEmpty(AgentData)) return null;

  return (
    <div
      className="bg-white p-4 md:p-6 rounded-xl"
      style={{ boxShadow: "0px 1px 4px 0px rgba(0,0,0,0.16)" }}
    >
      <h3 className="text-[#171717] font-bold text-xl md:text-2xl">
        {firstName}'s reviews
      </h3>
      <p className="text-[#000929] text-sm mt-1 opacity-75">
        Read the latest client reviews of {fullName}, real estate agent at{" "}
        {agencyName}.
      </p>

      <div className="flex gap-3 items-center mt-4">
        <LazyLoadImage
          src={AgentData?.profileImg}
          alt=""
          effect="blur"
          className="w-12 h-12 md:w-14 md:h-14 rounded-full object-cover flex-shrink-0 bg-[#F5F5F5]"
        />
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1.5">
            <FaStar className="text-[#FFB200] w-5 h-5 flex-shrink-0" />
            <span className="text-[#171717] font-bold text-lg">{rating}</span>
            <span className="text-[#000929] text-sm opacity-75">
              ({totalCount} reviews)
            </span>
          </div>
          <p className="text-[#525252] text-sm">
            Partnered together before?{" "}
            <button
              type="button"
              onClick={() => navigate(`/agent-review/${AgentData?._id}`)}
              className="text-[#097FE6] font-medium hover:underline"
            >
              Leave {firstName} a review
            </button>
          </p>
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-5">
        {displayReviews.map((item, index) => (
          <div key={item?.id ?? item?._id ?? index} className="flex gap-4">
            <div className="w-10 h-10 rounded-full bg-[#E5E5E5] flex items-center justify-center flex-shrink-0 text-[#525252] font-semibold text-sm uppercase tracking-tight">
              {getInitial(item)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="text-[#171717] font-bold text-base">
                  {getReviewerName(item)}
                </span>
                <span className="text-[#000929] text-sm opacity-75">
                  {formatReviewDate(item?.createdAt ?? item?.date)}
                </span>
              </div>
              <div className="flex items-center gap-1 mt-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <FaStar
                    key={i}
                    className={`w-4 h-4 flex-shrink-0 ${i < getReviewStars(item)
                      ? "text-[#FFB200]"
                      : "text-[#E5E5E5]"
                      }`}
                  />
                ))}
              </div>
              <p className="text-[#525252] text-sm md:text-base mt-2 leading-relaxed">
                {item?.review ?? item?.comment ?? item?.text ?? ""}
              </p>
            </div>
          </div>
        ))}
      </div>

      {hasMore && (
        <div className="flex justify-center mt-6">
          <button
            type="button"
            onClick={() => setShowAllReviews((p) => !p)}
            className="border border-[#E5E5E5] rounded-full bg-white text-[#525252] text-sm font-medium px-6 py-3 hover:bg-[#FAFAFA] transition-colors"
          >
            {showAllReviews ? "Show less" : `Show all ${totalCount} reviews`}
          </button>
        </div>
      )}
    </div>
  );
};

export default AgentReviewsCard;
